interface ServicesSectionProps {
  onGetQuote: () => void;
}

const services = [
  {
    number: "01",
    title: "General Contracting",
    description:
      "Full-scope general contracting for commercial and institutional projects. Taurus manages every phase — from site coordination to final handover — with precision, accountability, and a commitment to quality that shows in every finish.",
    image: "https://cdn.sanity.io/images/lncy278u/production/b4642a664cbb25c498205c0230877deeb8ff62ef-1000x589.jpg",
    tag: "Core Service",
  },
  {
    number: "02",
    title: "Design/Build Contracting",
    description:
      "A single-source solution that integrates design and construction under one team. Streamlined communication, faster delivery, and a cohesive build process from concept through completion.",
    image: "https://cdn.sanity.io/images/lncy278u/production/562b7b9a61a253d20f24fd3f1e16ef4734e7c4b7-1000x449.jpg",
    tag: "Integrated Delivery",
  },
  {
    number: "03",
    title: "Commercial Leasehold Improvements",
    description:
      "Transforming commercial spaces to meet tenant and business requirements. From office fit-outs to retail environments, Taurus delivers functional, well-finished interiors on schedule and on budget.",
    image: "https://cdn.sanity.io/images/lncy278u/production/248b8e7da1fe29bbd9a2be79062e72d2cc9d95a7-1000x667.jpg",
    tag: "Commercial Interiors",
  },
  {
    number: "04",
    title: "Pre Construction",
    description:
      "Strategic planning before a single nail is driven. Taurus provides budgeting, scheduling, constructability reviews, and value engineering to set every project up for a smooth, efficient build phase.",
    image: "https://cdn.sanity.io/images/lncy278u/production/1c03e29c6056dc0133917c054c6ebb1796beada5-1000x667.jpg",
    tag: "Planning & Strategy",
  },
  {
    number: "05",
    title: "Project Management",
    description:
      "Dedicated project management that keeps builds on track, on budget, and fully coordinated. Taurus acts as your single point of accountability — managing trades, timelines, and quality from start to finish.",
    image: "https://cdn.sanity.io/images/lncy278u/production/beb61ddedbbc50fa6fd019fbb69cfd94c393f5ae-823x491.jpg",
    tag: "Oversight & Delivery",
  },
];

export default function ServicesSection({ onGetQuote }: ServicesSectionProps) {
  return (
    <section id="services" className="w-full bg-white py-28 px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16 gap-6">
          <div>
            <span
              className="text-stone-400 text-xs uppercase tracking-[0.28em] font-medium block mb-4"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              What We Do
            </span>
            <h2
              className="text-stone-900 font-semibold leading-tight"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(2.2rem, 4vw, 3.4rem)",
              }}
            >
              Construction Services
              <br />
              <span className="font-light italic text-stone-600">Built for Performance</span>
            </h2>
          </div>
          <p
            className="text-stone-500 text-sm leading-relaxed max-w-sm lg:text-right"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            From pre-construction planning through final handover, Taurus delivers commercial construction with structural integrity, clean execution, and reliable timelines.
          </p>
        </div>

        {/* Services — alternating layout */}
        <div className="space-y-6">
          {/* Row 1: large left + small right */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Large card */}
            <div className="lg:col-span-3 group relative overflow-hidden rounded-sm bg-stone-900 min-h-[420px] flex flex-col justify-end cursor-default">
              <div className="absolute inset-0">
                <img
                  src={services[0].image}
                  alt={services[0].title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(10,10,10,0.88) 0%, rgba(10,10,10,0.3) 60%, transparent 100%)" }} />
              </div>
              <div className="relative z-10 p-8">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-stone-400 text-xs uppercase tracking-[0.2em]" style={{ fontFamily: "'Inter', sans-serif" }}>{services[0].tag}</span>
                  <div className="flex-1 h-px bg-stone-600" />
                  <span className="text-stone-500 text-xs font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{services[0].number}</span>
                </div>
                <h3
                  className="text-white font-semibold text-2xl md:text-3xl mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {services[0].title}
                </h3>
                <p className="text-stone-300 text-sm leading-relaxed max-w-md" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {services[0].description}
                </p>
              </div>
            </div>

            {/* Small card */}
            <div className="lg:col-span-2 group relative overflow-hidden rounded-sm bg-stone-900 min-h-[320px] lg:min-h-[420px] flex flex-col justify-end cursor-default">
              <div className="absolute inset-0">
                <img
                  src={services[1].image}
                  alt={services[1].title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(10,10,10,0.88) 0%, rgba(10,10,10,0.2) 60%, transparent 100%)" }} />
              </div>
              <div className="relative z-10 p-8">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-stone-400 text-xs uppercase tracking-[0.2em]" style={{ fontFamily: "'Inter', sans-serif" }}>{services[1].tag}</span>
                  <div className="flex-1 h-px bg-stone-600" />
                  <span className="text-stone-500 text-xs font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{services[1].number}</span>
                </div>
                <h3
                  className="text-white font-semibold text-2xl md:text-3xl mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {services[1].title}
                </h3>
                <p className="text-stone-300 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {services[1].description}
                </p>
              </div>
            </div>
          </div>

          {/* Row 2: small left + large right */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Small card */}
            <div className="lg:col-span-2 group relative overflow-hidden rounded-sm bg-stone-900 min-h-[320px] lg:min-h-[400px] flex flex-col justify-end cursor-default">
              <div className="absolute inset-0">
                <img
                  src={services[2].image}
                  alt={services[2].title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(10,10,10,0.88) 0%, rgba(10,10,10,0.2) 60%, transparent 100%)" }} />
              </div>
              <div className="relative z-10 p-8">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-stone-400 text-xs uppercase tracking-[0.2em]" style={{ fontFamily: "'Inter', sans-serif" }}>{services[2].tag}</span>
                  <div className="flex-1 h-px bg-stone-600" />
                  <span className="text-stone-500 text-xs font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{services[2].number}</span>
                </div>
                <h3
                  className="text-white font-semibold text-2xl md:text-3xl mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {services[2].title}
                </h3>
                <p className="text-stone-300 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {services[2].description}
                </p>
              </div>
            </div>

            {/* Large card */}
            <div className="lg:col-span-3 group relative overflow-hidden rounded-sm bg-stone-900 min-h-[320px] lg:min-h-[400px] flex flex-col justify-end cursor-default">
              <div className="absolute inset-0">
                <img
                  src={services[3].image}
                  alt={services[3].title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(10,10,10,0.88) 0%, rgba(10,10,10,0.2) 60%, transparent 100%)" }} />
              </div>
              <div className="relative z-10 p-8">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-stone-400 text-xs uppercase tracking-[0.2em]" style={{ fontFamily: "'Inter', sans-serif" }}>{services[3].tag}</span>
                  <div className="flex-1 h-px bg-stone-600" />
                  <span className="text-stone-500 text-xs font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{services[3].number}</span>
                </div>
                <h3
                  className="text-white font-semibold text-2xl md:text-3xl mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {services[3].title}
                </h3>
                <p className="text-stone-300 text-sm leading-relaxed max-w-md" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {services[3].description}
                </p>
              </div>
            </div>
          </div>

          {/* Row 3: full-width card for 5th service */}
          <div className="grid grid-cols-1 gap-6">
            <div className="group relative overflow-hidden rounded-sm bg-stone-900 min-h-[280px] flex flex-col justify-end cursor-default">
              <div className="absolute inset-0">
                <img
                  src={services[4].image}
                  alt={services[4].title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(10,10,10,0.88) 0%, rgba(10,10,10,0.15) 55%, transparent 100%)" }} />
              </div>
              <div className="relative z-10 p-8 lg:p-12 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-stone-400 text-xs uppercase tracking-[0.2em]" style={{ fontFamily: "'Inter', sans-serif" }}>{services[4].tag}</span>
                    <div className="w-16 h-px bg-stone-600" />
                    <span className="text-stone-500 text-xs font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{services[4].number}</span>
                  </div>
                  <h3
                    className="text-white font-semibold text-2xl md:text-3xl mb-3"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {services[4].title}
                  </h3>
                  <p className="text-stone-300 text-sm leading-relaxed max-w-2xl" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {services[4].description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-6 pt-10 border-t border-stone-100">
          <p className="text-stone-500 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
            Every project starts with a free, no-obligation consultation.
          </p>
          <button
            onClick={onGetQuote}
            className="flex items-center gap-3 px-10 py-4 bg-stone-900 text-white font-semibold text-sm uppercase tracking-[0.12em] rounded-sm hover:bg-stone-700 transition-all duration-300 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Request Your Estimate
            <i className="ri-arrow-right-line text-sm"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
